import type { CatalogueApiService, SearchCriteria } from '@angular-workspace/services';

export type CanvasDatasetAccess = 'Open' | 'Private' | 'Restricted';

export type OgcResourceApiType =
  | 'FEATURES'
  | 'STAC'
  | 'VECTOR_TILES'
  | 'RASTER_TILES';

/** Canvas catalogue filter options (resourceServer.queryTypes values). */
export const CANVAS_QUERY_TYPE_OPTIONS = [
  { label: 'Features', value: 'FEATURE' },
  { label: 'STAC', value: 'STAC' },
] as const;

/** Default canvas dataset scope — excludes vector/raster tile collections. */
export const DEFAULT_CANVAS_QUERY_TYPES = ['FEATURE', 'STAC'] as const;

export interface ValueDescriptor {
  type?: string[];
  description?: string;
  dataSchema?: string;
  unitCode?: string;
  unitText?: string;
}

export type DataDescriptor = Record<
  string,
  ValueDescriptor | string | string[] | undefined
>;

/** Keys in a dataDescriptor that are metadata — not filterable attributes. */
export const DESCRIPTOR_SKIP_KEYS = new Set([
  '@context',
  'type',
  'dataDescriptorLabel',
  'description',
  'geometry',
]);

export interface CanvasDataset {
  id: string;
  name: string;
  provider: string;
  access: CanvasDatasetAccess;
  /** Raw catalogue accessPolicy value (e.g. OPEN, RESTRICTED). */
  accessPolicy: string;
  /** OGC Features resource server base URL when available on the catalogue item. */
  resourceServerUrl?: string;
  /** STAC resource server base URL (from catalogue resourceServer entry). */
  stacServerUrl?: string;
  /** Vector/raster tile resource server base URL. */
  tileServerUrl?: string;
  /** Declared OGC API types for this dataset. */
  ogcResourceApis: string[];
  /** Primary API type for UI branching and filters. */
  resourceApiType: OgcResourceApiType | string;
}

export function extractCatalogueItemsFromResponse(response: unknown): unknown[] {
  if (!response) {
    return [];
  }

  if (Array.isArray(response)) {
    return response;
  }

  if (typeof response !== 'object') {
    return [];
  }

  const record = response as Record<string, unknown>;
  const result = record['result'];

  if (Array.isArray(result)) {
    return result;
  }

  if (result && typeof result === 'object') {
    const nested = result as Record<string, unknown>;
    if (Array.isArray(nested['results'])) {
      return nested['results'];
    }
  }

  if (Array.isArray(record['data'])) {
    return record['data'];
  }

  if (Array.isArray(record['items'])) {
    return record['items'];
  }

  return [];
}

export function mapAccessPolicyToCanvasAccess(accessPolicy: unknown): CanvasDatasetAccess {
  const value = String(accessPolicy ?? '').toLowerCase();
  if (value === 'open' || value === 'public') {
    return 'Open';
  }
  if (value === 'restricted') {
    return 'Restricted';
  }
  return 'Private';
}

function normalizeResourceServerUrl(raw: unknown): string {
  const value = String(raw ?? '').trim();
  if (!value) return '';
  if (/^https?:\/\//i.test(value)) {
    return value.replace(/\/+$/, '');
  }
  return `https://${value.replace(/\/+$/, '')}`;
}

function getResourceServerEntries(
  item: Record<string, unknown>,
): Record<string, unknown>[] {
  const rs = item['resourceServer'];
  if (Array.isArray(rs)) {
    return rs.filter(
      (e): e is Record<string, unknown> => e != null && typeof e === 'object',
    );
  }
  if (rs && typeof rs === 'object') {
    return [rs as Record<string, unknown>];
  }
  return [];
}

function entryMatches(
  record: Record<string, unknown>,
  namePattern: RegExp,
  queryPattern: RegExp,
): boolean {
  const name = String(record['name'] ?? '').toUpperCase();
  const queryTypes = Array.isArray(record['queryTypes'])
    ? record['queryTypes'].map((t) => String(t).toUpperCase())
    : [];
  return (
    namePattern.test(name) ||
    queryTypes.some((t) => queryPattern.test(t))
  );
}

function extractResourceServerUrlByPattern(
  item: Record<string, unknown>,
  namePattern: RegExp,
  queryPattern: RegExp,
): string {
  const entries = getResourceServerEntries(item);

  for (const entry of entries) {
    const url = normalizeResourceServerUrl(entry['url']);
    if (url && entryMatches(entry, namePattern, queryPattern)) {
      return url;
    }
  }

  for (const entry of entries) {
    const url = normalizeResourceServerUrl(entry['url']);
    if (url) return url;
  }

  return '';
}

/** Prefer OGC FEATURES server URL from catalogue `resourceServer` entries. */
export function extractOgcFeatureServerUrl(item: Record<string, unknown>): string {
  return extractResourceServerUrlByPattern(
    item,
    /OGC|FEATURE/i,
    /FEATURE/i,
  );
}

/** STAC server URL from catalogue `resourceServer` entries. */
export function extractStacServerUrl(item: Record<string, unknown>): string {
  return extractResourceServerUrlByPattern(item, /STAC/i, /STAC/i);
}

/** Vector/raster tile server URL from catalogue `resourceServer` entries. */
export function extractTileServerUrl(item: Record<string, unknown>): string {
  const entries = getResourceServerEntries(item);

  for (const entry of entries) {
    const url = normalizeResourceServerUrl(entry['url']);
    if (
      url &&
      entryMatches(entry, /TILE|VECTOR|RASTER/i, /TILE|VECTOR|RASTER/i)
    ) {
      return url;
    }
  }

  for (const entry of entries) {
    const url = normalizeResourceServerUrl(entry['url']);
    if (url && entryMatches(entry, /OGC/i, /TILE/i)) {
      return url;
    }
  }

  return extractOgcFeatureServerUrl(item);
}

/** Read declared API types; infer from resourceServer when missing. */
export function extractOgcResourceApis(item: Record<string, unknown>): string[] {
  const info = item['ogcResourceInfo'];
  if (info && typeof info === 'object' && !Array.isArray(info)) {
    const apis = (info as Record<string, unknown>)['ogcResourceAPIs'];
    if (Array.isArray(apis) && apis.length > 0) {
      return apis.map((a) => String(a).trim().toUpperCase()).filter(Boolean);
    }
  }

  const inferred: string[] = [];
  const entries = getResourceServerEntries(item);
  for (const entry of entries) {
    const name = String(entry['name'] ?? '').toUpperCase();
    const queryTypes = Array.isArray(entry['queryTypes'])
      ? entry['queryTypes'].map((t) => String(t).toUpperCase())
      : [];
    if (name.includes('STAC') || queryTypes.some((t) => t.includes('STAC'))) {
      inferred.push('STAC');
    }
    if (
      name.includes('RASTER') ||
      queryTypes.some((t) => t.includes('RASTER'))
    ) {
      inferred.push('RASTER_TILES');
    } else if (
      name.includes('VECTOR') ||
      name.includes('TILE') ||
      queryTypes.some((t) => t.includes('VECTOR') || t.includes('TILE'))
    ) {
      inferred.push('VECTOR_TILES');
    }
    if (
      name.includes('OGC') ||
      name.includes('FEATURE') ||
      queryTypes.some((t) => t.includes('FEATURE'))
    ) {
      inferred.push('FEATURES');
    }
  }

  return [...new Set(inferred)];
}

/** Primary API type for UI (STAC-first when declared, matching ui-canvas). */
export function resolveResourceApiType(
  apis: string[],
): OgcResourceApiType | string {
  if (apis.length === 0) {
    return 'FEATURES';
  }
  if (apis.includes('STAC') && apis[0] === 'STAC') {
    return 'STAC';
  }
  if (apis.includes('RASTER_TILES')) {
    return 'RASTER_TILES';
  }
  if (apis.includes('VECTOR_TILES')) {
    return 'VECTOR_TILES';
  }
  if (apis.includes('STAC')) {
    return 'STAC';
  }
  return apis[0] ?? 'FEATURES';
}

/** Single catalogue item record from GET `{catApiPath}item?id=…` (or search result row). */
export function extractCatalogueItemRecord(
  response: unknown,
): Record<string, unknown> | null {
  if (!response || typeof response !== 'object') {
    return null;
  }
  const record = response as Record<string, unknown>;
  const result = record['result'];
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    return result as Record<string, unknown>;
  }
  const items = extractCatalogueItemsFromResponse(response);
  if (items.length > 0 && items[0] && typeof items[0] === 'object') {
    return items[0] as Record<string, unknown>;
  }
  return null;
}

export function mapCatalogueItemToCanvasDataset(item: unknown): CanvasDataset | null {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const record = item as Record<string, unknown>;
  const id = String(record['id'] ?? '').trim();
  if (!id) {
    return null;
  }

  const name = String(record['label'] ?? record['name'] ?? 'Untitled').trim();
  const provider = String(
    record['organization'] ?? record['organisation'] ?? record['provider'] ?? '',
  ).trim();

  const accessPolicy = String(record['accessPolicy'] ?? '').trim();
  const resourceServerUrl = extractOgcFeatureServerUrl(record);
  const stacServerUrl = extractStacServerUrl(record);
  const tileServerUrl = extractTileServerUrl(record);
  const ogcResourceApis = extractOgcResourceApis(record);
  const resourceApiType = resolveResourceApiType(ogcResourceApis);

  return {
    id,
    name,
    provider,
    access: mapAccessPolicyToCanvasAccess(accessPolicy),
    accessPolicy,
    resourceApiType,
    ogcResourceApis,
    ...(resourceServerUrl ? { resourceServerUrl } : {}),
    ...(stacServerUrl ? { stacServerUrl } : {}),
    ...(tileServerUrl ? { tileServerUrl } : {}),
  };
}

export function mapCatalogueResponseToCanvasDatasets(
  response: unknown,
): CanvasDataset[] {
  return extractCatalogueItemsFromResponse(response)
    .map(mapCatalogueItemToCanvasDataset)
    .filter((item): item is CanvasDataset => item != null);
}

export function truncateDatasetName(name: string, maxLength = 30): string {
  if (name.length <= maxLength) {
    return name;
  }
  return `${name.slice(0, maxLength - 3)}...`;
}

/** Whether a catalogue row matches a canvas query-type filter value. */
export function datasetMatchesCanvasQueryType(
  dataset: CanvasDataset,
  queryType: string,
): boolean {
  const target = queryType.trim().toUpperCase();
  if (!target) {
    return false;
  }

  const apis = (dataset.ogcResourceApis ?? []).map((a) => a.toUpperCase());
  if (target === 'FEATURE') {
    return (
      apis.includes('FEATURES') ||
      apis.some((a) => a.includes('FEATURE')) ||
      String(dataset.resourceApiType).toUpperCase() === 'FEATURES'
    );
  }
  if (target === 'STAC') {
    return (
      apis.includes('STAC') ||
      String(dataset.resourceApiType).toUpperCase() === 'STAC'
    );
  }

  return (
    apis.includes(target) ||
    String(dataset.resourceApiType).toUpperCase() === target
  );
}

/** Whether dataset supports OGC Features load actions (Get All, bbox, attr filter). */
export function datasetSupportsFeaturesLoad(dataset: CanvasDataset): boolean {
  const apis = dataset.ogcResourceApis ?? [];
  if (apis.length === 0) {
    return Boolean(dataset.resourceServerUrl);
  }
  return apis.includes('FEATURES') || (!apis.includes('STAC') && apis.length > 0 && Boolean(dataset.resourceServerUrl));
}

/** ui-canvas: extra buttons show STAC when `ogcResourceAPIs[0] === 'STAC'`. */
export function datasetIsStacPrimary(dataset: CanvasDataset): boolean {
  const apis = dataset.ogcResourceApis ?? [];
  return apis[0] === 'STAC';
}

/** Show Load Tiles when tile API is primary (first in list). */
export function datasetIsTilesPrimary(dataset: CanvasDataset): boolean {
  const apis = dataset.ogcResourceApis ?? [];
  const first = apis[0];
  return first === 'VECTOR_TILES' || first === 'RASTER_TILES';
}

/** Whether any load action is available for this dataset row. */
export function datasetCanLoadActions(dataset: CanvasDataset): boolean {
  return Boolean(
    dataset.resourceServerUrl?.trim() ||
      dataset.stacServerUrl?.trim() ||
      dataset.tileServerUrl?.trim(),
  );
}

/** Catalogue criterion: only published (active) datasets in canvas search/list flows. */
export const CANVAS_PUBLISHED_SEARCH_CRITERION: SearchCriteria = {
  searchType: 'term',
  field: 'publishStatus',
  values: ['ACTIVE'],
};

/** Geospatial canvas maps: OGC datasets with FEATURE or STAC resource server query types. */
export const CANVAS_OGC_QUERY_TYPES_SEARCH_CRITERION: SearchCriteria = {
  searchType: 'flattenedTerm',
  field: 'resourceServer.queryTypes',
  values: [...DEFAULT_CANVAS_QUERY_TYPES],
};

/** Append the published-only criterion to catalogue search criteria. */
export function withCanvasPublishedCriterion(
  criteria: SearchCriteria[],
): SearchCriteria[] {
  return [...criteria, CANVAS_PUBLISHED_SEARCH_CRITERION];
}

/** True when a catalogue item record is published (active). */
export function isCatalogueItemPublished(
  record: Record<string, unknown> | null | undefined,
): boolean {
  if (!record) {
    return false;
  }
  const status = String(record['publishStatus'] ?? record['itemStatus'] ?? '')
    .trim()
    .toUpperCase();
  return status === 'ACTIVE';
}

/**
 * Resolve which dataset ids from a candidate list are published in the catalogue.
 * Used to filter OGC bbox discovery hits to active datasets only.
 */
export async function fetchPublishedDatasetIds(
  catalogueApi: CatalogueApiService,
  ids: string[],
): Promise<Set<string>> {
  const unique = [...new Set(ids.map((id) => id.trim()).filter(Boolean))];
  if (unique.length === 0) {
    return new Set();
  }

  const searchCriteria: SearchCriteria[] = [
    CANVAS_PUBLISHED_SEARCH_CRITERION,
    { searchType: 'term', field: 'id', values: unique },
  ];

  const response = await catalogueApi.searchAllAssets(
    '',
    ['Dataset'],
    [],
    1,
    unique.length,
    'itemCreatedAt:desc',
    searchCriteria,
  );

  const published = new Set<string>();
  for (const item of extractCatalogueItemsFromResponse(response)) {
    if (!item || typeof item !== 'object') {
      continue;
    }
    const record = item as Record<string, unknown>;
    if (!isCatalogueItemPublished(record)) {
      continue;
    }
    const id = String(record['id'] ?? '').trim();
    if (id) {
      published.add(id);
    }
  }
  return published;
}
